import React, { useRef } from "react";
import { X, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";

// Palette (matches the app)
const C = {
  ink: "#0F172A", ink2: "#475569", ink3: "#94A3B8", line: "#E2E8F0",
  indigo: "#4060FF", violet: "#7C3AED", fuchsia: "#C026D3", blue: "#2563EB",
  emerald: "#059669", amber: "#D97706", rose: "#DC2626",
};

function Box({ x, y, w, h, fill, stroke, title, sub, tColor = C.ink, tSize = 14, sSize = 11, r = 10 }) {
  const cx = x + w / 2;
  const hasSub = !!sub;
  const ty = hasSub ? y + h / 2 - 3 : y + h / 2 + tSize / 3;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={r} fill={fill} stroke={stroke} strokeWidth="1.6" />
      <text x={cx} y={ty} textAnchor="middle" fontSize={tSize} fontWeight="700" fill={tColor}>{title}</text>
      {hasSub && <text x={cx} y={y + h / 2 + 14} textAnchor="middle" fontSize={sSize} fill={C.ink2}>{sub}</text>}
    </g>
  );
}

function Diamond({ cx, cy, rx, ry, fill, stroke, lines }) {
  const pts = `${cx},${cy - ry} ${cx + rx},${cy} ${cx},${cy + ry} ${cx - rx},${cy}`;
  const start = cy - (lines.length - 1) * 8;
  return (
    <g>
      <polygon points={pts} fill={fill} stroke={stroke} strokeWidth="1.8" />
      <text x={cx} y={start + 4} textAnchor="middle" fontSize="12" fontWeight="700" fill={C.ink}>
        {lines.map((l, i) => <tspan key={i} x={cx} dy={i === 0 ? 0 : 16}>{l}</tspan>)}
      </text>
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, label, color = C.ink3, dash }) {
  const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.8"
        markerEnd="url(#jm-arrow)" strokeDasharray={dash ? "5 4" : undefined} />
      {label && (
        <>
          <rect x={mx - 12} y={my - 17} width="24" height="15" rx="3" fill="#fff" opacity="0.92" />
          <text x={mx} y={my - 6} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={color}>{label}</text>
        </>
      )}
    </g>
  );
}

// A left-aligned bullet column inside the onboarding box.
function Col({ x, y, w, header, items, hColor }) {
  return (
    <g>
      <text x={x} y={y} fontSize="11.5" fontWeight="700" fill={hColor}>{header}</text>
      {items.map((it, i) => (
        <text key={i} x={x} y={y + 20 + i * 16} fontSize="10.5" fill={C.ink2}>{`•  ${it}`}</text>
      ))}
    </g>
  );
}

export function JourneyMapSVG({ svgRef }) {
  // 3 columns
  const w = 346, x1 = 40, x2 = 417, x3 = 794;
  const c1 = x1 + w / 2, c2 = x2 + w / 2, c3 = x3 + w / 2; // 213, 590, 967
  return (
    <svg ref={svgRef} viewBox="0 0 1180 1040" width="100%" style={{ height: "auto", background: "#fff", fontFamily: "Montserrat, system-ui, sans-serif" }}>
      <defs>
        <marker id="jm-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3.2" orient="auto">
          <path d="M0,0 L7,3.2 L0,6.4 Z" fill={C.ink3} />
        </marker>
      </defs>

      {/* Title */}
      <text x="40" y="34" fontSize="23" fontWeight="800" fill={C.ink}>How someone becomes a member</text>
      <text x="40" y="58" fontSize="13" fill={C.ink2}>Every visitor is a Prospect first. Membership happens in one place only — Wallets — once their own CaryPact link is verified.</text>
      {/* legend */}
      <g transform="translate(880,20)">
        <rect x="0" y="0" width="16" height="12" rx="3" fill="#EEF1FF" stroke={C.indigo} />
        <text x="22" y="10" fontSize="10.5" fill={C.ink2}>step</text>
        <polygon points="80,6 90,0 100,6 90,12" fill="#FFF4E6" stroke={C.amber} />
        <text x="106" y="10" fontSize="10.5" fill={C.ink2}>decision</text>
        <rect x="170" y="0" width="16" height="12" rx="3" fill="#E9FBF3" stroke={C.emerald} />
        <text x="192" y="10" fontSize="10.5" fill={C.ink2}>member</text>
      </g>

      {/* Step 1 label */}
      <text x="40" y="82" fontSize="11.5" fontWeight="700" fill={C.indigo}>1 · They open a link their sponsor shared or emailed</text>

      {/* Doors */}
      <Box x={x1} y={90} w={w} h={80} fill="#F3EEFF" stroke={C.violet} title="Discover" sub="/discover · watch, no sign-up needed" />
      <Box x={x2} y={90} w={w} h={80} fill="#FCEEFF" stroke={C.fuchsia} title="Sizzle → Funnel" sub="/watch · quick sizzle, then setup" />
      <Box x={x3} y={90} w={w} h={80} fill="#EAF2FF" stroke={C.blue} title="Funnel" sub="/join · straight to account setup" />

      {/* Doors -> watch boxes */}
      <Arrow x1={c1} y1={170} x2={c1} y2={196} />
      <Arrow x1={c2} y1={170} x2={c2} y2={196} />
      <Arrow x1={c3} y1={170} x2={c3} y2={196} />

      {/* Watch boxes */}
      <Box x={x1} y={196} w={w} h={50} fill="#F8FAFC" stroke={C.line} title="Watch sizzle → full presentation" tSize={12} />
      <Box x={x2} y={196} w={w} h={50} fill="#F8FAFC" stroke={C.line} title="Watch a quick sizzle" tSize={12} />
      <Box x={x3} y={196} w={w} h={50} fill="#F8FAFC" stroke={C.line} title="No videos — setup only" tSize={12} />

      {/* Watch -> converge */}
      <Arrow x1={c1} y1={246} x2={c1} y2={282} />
      <Arrow x1={c2} y1={246} x2={c2} y2={282} />
      <Arrow x1={c3} y1={246} x2={c3} y2={282} />

      {/* Converge bar */}
      <Box x={40} y={282} w={1100} h={52} fill="#EEF1FF" stroke={C.indigo}
        title="Recorded as a Prospect" sub="status moves: Clicked link → Watching → Watched  (shows in your Prospects table)" tColor={C.indigo} />

      <Arrow x1={590} y1={334} x2={590} y2={360} />

      {/* Onboarding box */}
      <rect x={40} y={360} width={1100} height={184} rx={12} fill="#fff" stroke={C.indigo} strokeWidth="1.8" />
      <text x="60" y="384" fontSize="13.5" fontWeight="800" fill={C.ink}>2 · Onboarding — guided wallet + CaryPact setup</text>
      <Col x={70} y={410} w={300} hColor={C.indigo} header="Intro & readiness"
        items={["Watch intro, agree disclaimer", "Confirm ready + set location"]} />
      <Col x={430} y={410} w={340} hColor={C.violet} header="Section A · Wallet"
        items={["Install TokenPocket wallet", "Back up 12-word phrase", "Click sponsor's CaryPact link", "Import USDT + CA tokens"]} />
      <Col x={800} y={410} w={340} hColor={C.emerald} header="Section B · Fund & earn"
        items={["Fund wallet (USDT + gas)", "Buy hashrate on BOT Chain", "Stake CA", "Copy their OWN CaryPact link"]} />
      <rect x={60} y={510} width={1060} height={22} rx={6} fill="#FFF7ED" stroke={C.amber} />
      <text x={590} y={525} textAnchor="middle" fontSize="11.5" fontWeight="700" fill={C.amber}>Finishes by submitting their OWN CaryPact invite link</text>

      <Arrow x1={590} y1={544} x2={590} y2={554} />

      {/* Decision 1 */}
      <Diamond cx={590} cy={614} rx={100} ry={60} fill="#FFF4E6" stroke={C.amber}
        lines={["Submitted their own", "CaryPact link?"]} />
      {/* No branch */}
      <Arrow x1={690} y1={614} x2={800} y2={614} label="No" color={C.amber} />
      <Box x={800} y={589} w={340} h={50} fill="#F1F5F9" stroke={C.ink3}
        title="Still a Prospect — nudge them" tSize={12} />

      {/* Decision 1 -> 2 */}
      <Arrow x1={590} y1={674} x2={590} y2={686} label="Yes" color={C.emerald} />

      {/* Decision 2 */}
      <Diamond cx={590} cy={746} rx={100} ry={60} fill="#FFF4E6" stroke={C.amber}
        lines={["Wallet synced in tree", "& not already claimed?"]} />
      {/* No branch */}
      <Arrow x1={690} y1={746} x2={800} y2={746} label="No" color={C.amber} />
      <Box x={800} y={721} w={340} h={50} fill="#FFF4E6" stroke={C.amber}
        title="Waiting for wallet to sync" tSize={12} tColor={C.amber} />

      {/* left note */}
      <rect x={40} y={710} width={300} height={78} rx={10} fill="#FFF7ED" stroke={C.amber} strokeWidth="1.4" />
      <text x={56} y={732} fontSize="11.5" fontWeight="700" fill={C.amber}>Sign-in requests</text>
      <text x={56} y={750} fontSize="10.5" fill={C.ink2}>A login request only appears once</text>
      <text x={56} y={764} fontSize="10.5" fill={C.ink2}>their own CaryPact wallet has synced.</text>
      <text x={56} y={778} fontSize="10.5" fill={C.ink2}>No wallet + link = no request.</text>

      <Arrow x1={590} y1={806} x2={590} y2={826} label="Yes" color={C.emerald} />

      {/* Ready */}
      <Box x={x2} y={826} w={w} h={48} fill="#E9FBF3" stroke={C.emerald}
        title="Ready to make a member" tSize={13} tColor={C.emerald} />
      <Arrow x1={590} y1={874} x2={590} y2={894} />

      {/* Admin action */}
      <Box x={x2} y={894} w={w} h={48} fill="#EEF1FF" stroke={C.indigo}
        title="Admin: Wallets → confirm email → +" tSize={12} tColor={C.indigo} />
      <Arrow x1={590} y1={942} x2={590} y2={962} />

      {/* Member */}
      <rect x={x2} y={962} width={w} height={50} rx={10} fill={C.emerald} />
      <text x={590} y={992} textAnchor="middle" fontSize="15" fontWeight="800" fill="#fff">✓ Member — own wallet + link</text>

      <text x="40" y="1030" fontSize="10" fill={C.ink3}>BotChainCP · Prospect → Onboarding → verified CaryPact link → made a member in Wallets. (Academy is retired.)</text>
    </svg>
  );
}

export default function JourneyMap({ onClose }) {
  const svgRef = useRef(null);

  const printMap = () => {
    const svg = svgRef.current;
    if (!svg) return;
    const win = window.open("", "_blank", "width=1240,height=980");
    if (!win) return;
    win.document.write(
      `<html><head><title>BotChainCP — Journey Map</title>
       <style>@page{size:landscape;margin:12mm}body{margin:0;font-family:Montserrat,system-ui,sans-serif;display:flex;align-items:center;justify-content:center}svg{width:100%;max-width:1180px;height:auto}</style>
       </head><body>${svg.outerHTML}</body></html>`
    );
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 350);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      style={{ background: "rgba(15,18,38,0.5)", backdropFilter: "blur(4px)" }}
      onClick={onClose} data-testid="journey-map-modal">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-black/5 flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-slate-900">Member journey map</div>
            <div className="text-xs text-ink-3">The whole flow on one page — how a prospect becomes a member.</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={printMap} data-testid="journey-map-print">
              <Printer size={14} className="mr-1.5" /> Save as PDF
            </Button>
            <button onClick={onClose} data-testid="journey-map-close" className="text-ink-3 hover:text-ink-1 p-1"><X size={18} /></button>
          </div>
        </div>
        <div className="p-3 md:p-5 overflow-x-auto">
          <JourneyMapSVG svgRef={svgRef} />
        </div>
      </div>
    </div>
  );
}
